import './auth-modal.scss';

import { Modal } from 'antd';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { ROUTES } from '@/components/Shared/routes/routes';
import { Button } from '@/components/Shared/ui';
import AllurFinanceLogo from '@/media/allur-finance.svg?react';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { useAppDispatch, useAppSelector } from '@/store';
import { login, setIsAuthErrorModal } from '@/store/auth.slice';
import { getUserAccount } from '@/store/userInfo.slice';

import { LoginFields } from '../../entities';
import { type IFormInput } from '../../types/form-types';

type IModalFormInput = Omit<IFormInput, 'dealer'>;

const AuthModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userInfo, dealer } = useAppSelector((state) => state.userInfo);
  const { isAuthErrorModal } = useAppSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<IModalFormInput>({
    mode: 'all',
    delayError: 200,
    defaultValues: {
      email: userInfo?.email || '',
      password: '',
      isRemember: false
    }
  });
  const {
    formState: { errors },
    reset,
    handleSubmit
  } = methods;

  const onSubmit = async (data: IModalFormInput) => {
    setIsLoading(true);

    const loginResponse = await dispatch(login(data));
    const userResponse = await dispatch(getUserAccount());
    if (
      getUserAccount.fulfilled.match(userResponse) &&
      userResponse.payload?.Users_Dealers &&
      userResponse.payload?.Users_Dealers.length > 0
    ) {
      const hasCurrentDealer = userResponse.payload.Users_Dealers.some(
        (userDealer) => userDealer.dealer.code === dealer?.code
      );

      if (!hasCurrentDealer) {
        // Если у нового пользователя нет текущего дилера, то выходим из системы
        dispatch(setIsAuthErrorModal('finally'));
        setIsLoading(false);
        reset();
        return;
      }
    }

    if (loginResponse.payload?.status) {
      reset();
      dispatch(setIsAuthErrorModal(''));
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    dispatch(setIsAuthErrorModal(''));
    clearLocalStorageHelper();
    const savedUrl =
      window.location.pathname !== ROUTES.login &&
      !window.location.pathname.includes('create')
        ? `${window.location.pathname}${window.location.search}`
        : '';
    navigate(ROUTES.login, { state: { savedUrl } });
  };

  return (
    <Modal
      centered
      mask={{ closable: true }}
      className="auth-modal"
      open={isAuthErrorModal !== ''}
      onCancel={handleClose}
      footer={null}
    >
      {isAuthErrorModal === 'finally' && (
        <>
          <div className="auth-modal__header">
            <AllurFinanceLogo className="auth-modal__logo" />
            <h2 className="auth-modal__title">Ошибка авторизации!</h2>
          </div>
          <p className="auth-modal__description">
            Ваша сессия истекла. Пожалуйста, войдите в систему.
          </p>

          <Button variant="filled-red" fullWidth onClick={handleClose}>
            Продолжить
          </Button>
        </>
      )}
      {isAuthErrorModal === 'reauthenticate' && (
        <FormProvider {...methods}>
          <form className="auth-modal__form" onSubmit={handleSubmit(onSubmit)}>
            <div className="auth-modal__header">
              <AllurFinanceLogo className="auth-modal__logo" />
              <h2 className="auth-modal__title">Вход в систему</h2>
            </div>
            <p className="auth-modal__description">
              Ваша сессия истекла. Пожалуйста, войдите в систему.
            </p>

            <LoginFields />

            <Button
              type="submit"
              className="auth-modal__submit"
              variant="filled-red"
              disabled={Object.values(errors).length !== 0}
              isLoading={isLoading}
              fullWidth
            >
              Войти
            </Button>
          </form>
        </FormProvider>
      )}
    </Modal>
  );
};

export default AuthModal;
